import '../admin-page/admin-page.css';
import './lost-reason-config-page.css';
import { Header } from '../header/header';
import { Menu, Spin } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { MenuProps } from 'antd';
import { useMsal } from '@azure/msal-react';
import { scopes } from '../../authConfig';
import { GetAccessToken } from '../../utils/auth-utils';
import { HandoverSystemDto, LostReasonChannelConfigurationAdminDto, LostReasonChannelGroupAdminDto, LostReasonDto, SourceSystemLostReasonDto } from '../../dto/lost-reason-models';
import { getHandoverSystems, getLostReasonChannelAdmin, getLostReasons, getSourceSystemLostReasons } from '../../services/lost-reason-service';
import { LostReasonsTab } from './lost-reasons-tab';
import { LostReasonGroupsTab } from './lost-reason-groups-tab';
import { SourceSystemLostReasonsTab } from './source-system-lost-reasons-tab';
import { HandoverSystemsTab } from './handover-systems-tab';

type MenuItem = Required<MenuProps>['items'][number];

export const LostReasonConfigPage = () => {
  const { instance, inProgress } = useMsal();
  const [selectedKey, setSelectedKey] = useState<string>('1');
  const [loading, setLoading] = useState(true);
  const [sourceSystemReasons, setSourceSystemReasons] = useState<SourceSystemLostReasonDto[]>([]);
  const [reasons, setReasons] = useState<LostReasonDto[]>([]);
  const [configurations, setConfigurations] = useState<LostReasonChannelConfigurationAdminDto[]>([]);
  const [groups, setGroups] = useState<LostReasonChannelGroupAdminDto[]>([]);
  const [handoverSystems, setHandoverSystems] = useState<HandoverSystemDto[]>([]);
  const initialLoadRef = useRef(true);

  const refresh = useCallback(async () => {
    const token = await GetAccessToken(instance, inProgress, scopes.salesAppointmentApi);
    const [sfRes, rRes, adminRes, hsRes] = await Promise.all([
      getSourceSystemLostReasons(token.accessToken, false),
      getLostReasons(token.accessToken, false),
      getLostReasonChannelAdmin(token.accessToken),
      getHandoverSystems(token.accessToken, false),
    ]);
    if (sfRes.isSuccess) setSourceSystemReasons(sfRes.data ?? []);
    if (rRes.isSuccess) setReasons(rRes.data ?? []);
    if (adminRes.isSuccess && adminRes.data) {
      setConfigurations(adminRes.data.configurations ?? []);
      setGroups(adminRes.data.groups ?? []);
    }
    if (hsRes.isSuccess) setHandoverSystems(hsRes.data ?? []);
  }, [instance, inProgress]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      if (initialLoadRef.current) setLoading(true);
      try {
        await refresh();
      } finally {
        if (!cancelled && initialLoadRef.current) {
          setLoading(false);
          initialLoadRef.current = false;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [refresh]);

  const items: MenuItem[] = [
    { key: '1', label: 'Source system lost reasons' },
    { key: '2', label: 'BOS lost reasons' },
    {
      key: '3',
      label: (
        <span style={{ whiteSpace: 'normal', lineHeight: 1.35, display: 'inline-block' }}>
          Configurations & outcomes
        </span>
      ),
    },
    { key: '4', label: 'Handover systems' },
  ];

  return (
    <>
      <Header />
      <div className="flex-admin-container">
        <Menu
          className="lost-reason-config-sider-menu"
          selectedKeys={[selectedKey]}
          mode="inline"
          style={{ width: 248, minWidth: 248, flexShrink: 0, height: 'fit-content' }}
          onSelect={(item) => setSelectedKey(String(item.key))}
          items={items}
        />
        {loading ? (
          <div
            style={{
              flex: 1,
              minWidth: 0,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: 400,
            }}
          >
            <Spin size="large" tip="Loading configuration…" />
          </div>
        ) : (
          <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
            {selectedKey === '1' && (
              <SourceSystemLostReasonsTab reasons={sourceSystemReasons} bosReasons={reasons} onRefresh={refresh} />
            )}
            {selectedKey === '2' && <LostReasonsTab reasons={reasons} onRefresh={refresh} />}
            {selectedKey === '3' && (
              <LostReasonGroupsTab
                configurations={configurations}
                groups={groups}
                reasonLibrary={reasons}
                handoverSystems={handoverSystems}
                onRefresh={refresh}
              />
            )}
            {selectedKey === '4' && <HandoverSystemsTab handoverSystems={handoverSystems} onRefresh={refresh} />}
          </div>
        )}
      </div>
    </>
  );
};
